import React, { useRef } from "react";
import { Button } from "./Button";
import { cn } from "../../lib/cn";

let uid = 0;
const genUid = () => `file-${Date.now()}-${uid++}`;

/**
 * Enough antd Upload behavior for forms: emits { file, fileList } via onChange.
 * Controlled via `fileList` from Form.Item valuePropName="fileList"
 */
export function Upload({
  children,
  accept,
  showUploadList = true,
  beforeUpload,
  onChange,
  className,
  multiple,
  listType,
  maxCount,
  value: fileListProp,
  disabled,
}) {
  const inputRef = useRef(null);

  function emit(list) {
    const last = list[list.length - 1];
    onChange?.({ file: last, fileList: list });
  }

  async function handleFiles(fileListNative) {
    let list = [...(fileListProp || [])].map((x) =>
      typeof x === "object" ? x : { uid: genUid(), name: String(x) }
    );
    const incoming = [...fileListNative].slice(
      0,
      maxCount ? maxCount - list.length : undefined
    );
    for (const file of incoming) {
      let skip = false;
      if (beforeUpload) {
        const r = beforeUpload(file);
        if (r === false) skip = true;
        else if (r && typeof r.then === "function") {
          const ok = await r.catch(() => false);
          if (ok === false) skip = true;
        }
      }
      if (skip) continue;
      list.push({
        uid: genUid(),
        name: file.name,
        status: "done",
        originFileObj: file,
        url:
          typeof URL !== "undefined" && typeof file === "object"
            ? URL.createObjectURL(file)
            : undefined,
      });
    }
    emit(list);

    inputRef.current && (inputRef.current.value = "");
  }

  const list =
    Array.isArray(fileListProp) && fileListProp.length ? fileListProp : [];

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => handleFiles(e.target.files || [])}
      />
      <span className={disabled ? "pointer-events-none opacity-50" : ""}>
        {React.Children.count(children) ? (
          <span
            onClick={() => !disabled && inputRef.current?.click()}
            role="presentation"
          >
            {children}
          </span>
        ) : (
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
            Upload
          </Button>
        )}
      </span>
      {showUploadList &&
        listType === "picture" &&
        list.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {list.map((f) => (
              <div
                key={f.uid}
                className="h-14 w-14 overflow-hidden rounded-lg border border-slate-200"
              >
                {f.thumbUrl || f.url ? (
                  <img
                    alt=""
                    src={f.thumbUrl || f.url}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-slate-50 text-[10px] text-slate-500">
                    {f.name?.slice(0, 6)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
